import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Refresh, CheckCircle as SyncedIcon, Warning } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useLanguage } from '@/contexts/LanguageContext';

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const SyncStatusContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${props => props.$variant === 'inline' ? '6px' : '12px'};
    background: ${props => props.$variant === 'inline' ? 'transparent' : 'rgba(0, 0, 0, 0.2)'};
    border: ${props => props.$variant === 'inline' ? 'none' : '1px solid rgba(255, 255, 255, 0.05)'};
    padding: ${props => props.$variant === 'inline' ? '0' : '12px 16px 12px 10px'};
    border-radius: ${props => props.$variant === 'sidebar' ? '12px' : '14px'};
    transition: all 0.3s ease;
    margin-top: ${props => props.$variant === 'sidebar' ? 'auto' : '0'};

    ${props => props.$variant !== 'inline' && props.$status === 'syncing' && css`
        border-color: rgba(99, 102, 241, 0.3);
        background: rgba(99, 102, 241, 0.05);
    `}

    ${props => props.$variant !== 'inline' && (props.$status === 'synced' || props.$status === 'idle') && css`
        border-color: rgba(74, 222, 128, 0.2);
    `}
`;

const SyncIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.$variant === 'inline' ? '16px' : props.$variant === 'sidebar' ? '24px' : '32px'};
    height: ${props => props.$variant === 'inline' ? '16px' : props.$variant === 'sidebar' ? '24px' : '32px'};
    border-radius: 50%;
    
    ${props => props.$status === 'syncing' && css`
        color: #818cf8;
        animation: ${spin} 2s linear infinite;
    `}

    ${(props) => (props.$status === 'synced' || props.$status === 'idle') && css`
        color: #4ade80;
    `}

    ${props => props.$status === 'error' && css`
        color: #f87171;
    `}
`;

const SyncTextContent = styled.div`
    display: flex;
    flex-direction: ${props => props.$variant === 'inline' ? 'row' : 'column'};
    align-items: ${props => props.$variant === 'inline' ? 'center' : 'flex-start'};
    gap: ${props => props.$variant === 'inline' ? '5px' : '0'};
    text-align: left;
`;

const SyncTitle = styled.span`
    font-size: ${props => props.$variant === 'inline' ? '0.65rem' : props.$variant === 'sidebar' ? '0.7rem' : '0.75rem'};
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${props => props.$variant === 'inline' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.9)'};
`;

const SyncSubtitle = styled.span`
    font-size: ${props => props.$variant === 'inline' ? '0.65rem' : '0.7rem'};
    color: rgba(255, 255, 255, 0.4);
    font-weight: 500;
`;

const SyncStatusIndicator = ({ status, isSaving, variant = 'default' }) => {
    const { t } = useLanguage();

    // Local state to ensure the 'syncing' status is visible for at least a moment (prevent flashing)
    const [visibleStatus, setVisibleStatus] = useState(status || 'synced');

    useEffect(() => {
        if (status === 'syncing') {
            setVisibleStatus('syncing');
        } else {
            // When finishing sync, wait 800ms before showing 'synced' so the user sees the spinner
            const timer = setTimeout(() => {
                setVisibleStatus(status || 'synced');
            }, 1800);
            return () => clearTimeout(timer);
        }
    }, [status]);

    return (
        <Tooltip
            title={variant === 'sidebar' && visibleStatus === 'synced' ? t('SYNC_STATUS_TOOLTIP') : ''}
            placement="right"
            arrow
            disableHoverListener={variant !== 'sidebar'}
        >
            <SyncStatusContainer $status={visibleStatus} $variant={variant}>
                <SyncIconWrapper $status={visibleStatus} $variant={variant}>
                    {visibleStatus === 'syncing' && <Refresh style={{ fontSize: variant === 'inline' ? 14 : variant === 'sidebar' ? 18 : 20 }} />}
                    {(visibleStatus === 'synced' || visibleStatus === 'idle') && <SyncedIcon style={{ fontSize: variant === 'inline' ? 14 : variant === 'sidebar' ? 18 : 20 }} />}
                    {visibleStatus === 'error' && <Warning style={{ fontSize: variant === 'inline' ? 14 : variant === 'sidebar' ? 18 : 20 }} />}
                </SyncIconWrapper>
                <SyncTextContent $variant={variant}>
                    <SyncTitle $variant={variant}>
                        {visibleStatus === 'syncing' ? t('SYNCING_FILES') : t('FILES_UPDATED_IN_GAME')}
                    </SyncTitle>
                    <SyncSubtitle $variant={variant}>
                        {visibleStatus === 'syncing' ? t('UPDATING_GAME_CFG') : t('SYNCED_IN_GAME')}
                    </SyncSubtitle>
                </SyncTextContent>
            </SyncStatusContainer>
        </Tooltip>
    );
};

export default SyncStatusIndicator;
